// Headline card for one PEAK4-derived indicator (FED / LRM / CDI / PDC).
//
// FM-1 — the boxes used to carry the whole "What is X?" paragraph, which nobody
// reads at a glance. The card now shows a one-line summary plus the single
// clarifying sentence TW asked for (cardNote); the full description moved into
// the "{CODE} Breakdown" panel behind the button.

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { FatigueDerivedMetric } from '../data/types';
import { METRIC_DEFINITIONS } from '../data/methodology';
import { MetricRing, TierBadge, TrendPill } from './primitives';

export const KpiCard: React.FC<{
  metric: FatigueDerivedMetric;
  onOpenBreakdown: () => void;
}> = ({ metric, onOpenBreakdown }) => {
  const def = METRIC_DEFINITIONS[metric.key];

  return (
    <Card className='flex h-full flex-col'>
      <CardContent className='flex flex-1 flex-col gap-4 p-5'>
        <div className='flex items-start justify-between gap-3'>
          <div className='min-w-0'>
            <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{def.code}</p>
            <h3 className='text-sm font-semibold'>{def.name}</h3>
          </div>
          <TierBadge tier={metric.riskTier} />
        </div>

        <div className='flex items-center gap-4'>
          <MetricRing value={metric.fill} display={String(metric.value)} tier={metric.riskTier} size={76} />
          <div className='flex min-w-0 flex-col gap-1.5'>
            <p className='text-xs text-muted-foreground'>{metric.unit}</p>
            <TrendPill trend={metric.trend} label={metric.trendLabel} goodDirection={metric.goodDirection} />
            <p className='text-xs text-muted-foreground'>{metric.baselineLabel}</p>
          </div>
        </div>

        <div className='flex flex-1 flex-col gap-1.5'>
          <p className='text-sm font-medium'>{def.cardSummary}</p>
          <p className='text-sm text-muted-foreground'>{def.cardNote}</p>
        </div>

        <Button variant='outline' size='sm' className='w-full justify-between' onClick={onOpenBreakdown}>
          {def.code} Breakdown
          <ChevronRight className='size-4' aria-hidden />
        </Button>
      </CardContent>
    </Card>
  );
};
